import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { Card, Form, Button, Spinner, InputGroup } from "react-bootstrap";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faUser,
  faEye,
  faEyeSlash,
  faKey,
  faUnlock,
  faTrash,
  faAddressCard,
  faFlask,
} from "@fortawesome/free-solid-svg-icons";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import LogoutModal from "./LogoutModal";
import DeleteRestoreModal from "../../../components/DeleteRestoreModal";
import DeletedItemBanner from "components/common/DeletedItemBanner";
import DeletedBorderCard from "components/common/DeletedBorderCard";
import { usePermissionModal } from "contexts/PermissionModalContext";

import { EditarUsuarioDtoType, FormValues } from "./userTypes";
import InformacoesUsuario from "./InformacoesUsuario";
import LojasAssociadas from "./LojasAssociadas";
import { useLogout, useUser } from "services/account";
import { useToast } from "providers/ToastProvider";
import PermissoesPdv from "./permissoesPdv";
import AcessoAoSistema from "./acessoAoSistema";

// Define breadcrumbs for the user edit page
const usuarioBreadcrumbItems = () => [
  {
    label: "Retaguarda",
    url: "#!",
  },
  {
    label: "Cadastros",
    url: "#!",
  },
  {
    label: "Usuários",
    url: "/cadastros/usuarios",
  },
  {
    label: "Editar",
    active: true,
  },
];

const EditarUsuario = () => {
  const { id } = useParams<{ id: any }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { mutateAsync: logout } = useLogout();
  const { showToast } = useToast();

  // Usar o contexto para controlar o modal de permissão
  const { getMutationErrorHandler } = usePermissionModal();

  const [showPdvNovaSenha, setShowPdvNovaSenha] = useState(false);
  const [showPdvConfirmarSenha, setShowPdvConfirmarSenha] = useState(false);
  const [showFpPassword, setShowFpPassword] = useState(false);
  const [showSngpcPassword, setShowSngpcPassword] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const [modalOperation, setModalOperation] = useState<
    null | "delete" | "restore"
  >(null);

  // Configurar o formulário usando react-hook-form
  const { isLoading, isError, data, error, refetch } =
    useQuery<EditarUsuarioDtoType>({
      queryKey: ["usuario", id],
      queryFn: async () => {
        const response = await axios.get(`/api/usuario/${id}`);

        // Extract the actual user data from the Result wrapper
        const userData = response.data.data;

        if (!userData) {
          throw new Error(response.data.message || "Erro ao carregar usuário");
        }

        // Process empresasAssociadas if available
        if (
          userData.empresasAssociadas &&
          Array.isArray(userData.empresasAssociadas)
        ) {
          // Prepare storeAccess data structure based on empresasAssociadas
          const storeAccess = userData.empresasAssociadas.map(
            (item: { empresaId: any; local: boolean }) => ({
              empresaId: item.empresaId,
              hasAccess: true,
              isWorkLocation: item.local || false,
            })
          );

          // Set storeAccess field directly
          userData.storeAccess = storeAccess;
        }

        return userData;
      },
      enabled: !!id,
    });

  const formMethods = useForm<FormValues>({
    resetOptions: { keepDirtyValues: true },
    values: data
      ? {
          ...data,
          storeAccess:
            data.empresasAssociadas?.map((item) => ({
              empresaId: item.empresaId,
              hasAccess: true,
              isWorkLocation: item.local,
            })) || [],
          empresasAssociadas: data.empresasAssociadas || [],
          isEntregador: data.isEntregador ?? false,
          isVendedor: data.isVendedor ?? false,
          isSngpc: data.isSngpc ?? false,
          isSemAcesso: data.isSemAcesso ?? false,
          emailSngpc: data.emailSngpc ?? "",
          senhaSngpc: "",
          pdvNovaSenha: "",
          pdvSenha: "",
        }
      : undefined,
  });

  const isDirtyRef = useRef(false);

  useEffect(() => {
    isDirtyRef.current = formMethods.formState.isDirty;
  }, [formMethods.formState.isDirty]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!isDirtyRef.current) return false;
    if (formMethods.formState.isSubmitting) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;

    setPendingNavigation(nextLocation.pathname);
    setShowNavigationModal(true);

    return true;
  });

  // Function to handle navigation confirmation
  const handleNavigationConfirm = () => {
    setShowNavigationModal(false);

    if (pendingNavigation) {
      // Reset dirty state before navigating
      isDirtyRef.current = false;

      // Unblock and navigate
      blocker.proceed?.();
      navigate(pendingNavigation);
    }
  };

  // Function to cancel navigation
  const handleNavigationCancel = () => {
    setShowNavigationModal(false);
    blocker.reset?.();
  };

  const updateUserMutation = useMutation({
    mutationFn: async (formData: FormValues) => {
      const dataToSend = { ...formData };

      delete dataToSend.pdvNovaSenha;
      delete dataToSend.pdvConfirmarSenha;

      // Blank SNGPC password = do not update (backend ignores null)
      if (!dataToSend.senhaSngpc) {
        dataToSend.senhaSngpc = undefined;
      }

      // Simplify the empresas logic to consistently use storeAccess
      // storeAccess is the newer format that includes both access and work location flags
      const storeAccess = formMethods.getValues("storeAccess") || [];

      // Only include empresas where the user has access
      const empresas = storeAccess
        .filter((sa) => sa.hasAccess)
        .map((sa) => ({
          EmpresaId: sa.empresaId,
          Local: sa.isWorkLocation || false,
        }));

      return axios.put(`/api/usuario/${id}`, {
        ...dataToSend,
        senha: formData.novaSenha,
        empresasAssociadas: empresas,
      });
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para editar usuários",
      (error) => {
        showToast(
          error.response?.data?.message || "Erro ao atualizar usuário",
          "error"
        );
      }
    ),
    onSuccess: (response) => {
      if (response.data.isSuccess) {
        const codigoUsuario =
          response.data.data?.codigo || formMethods.getValues("codigo");

        // Reset the form's dirty state after successful save
        formMethods.reset(formMethods.getValues(), {
          keepValues: true,
          keepDirtyValues: false,
        });

        // Also reset our dirty ref to ensure navigation works properly
        isDirtyRef.current = false;

        // Check if the updated user is the same as the logged-in user
        if (user?.email === formMethods.getValues("email")) {
          // Show logout confirmation modal
          setShowLogoutModal(true);
        } else {
          // Navigate back to users list with success state
          navigate("/cadastros/usuarios", {
            state: {
              successCode: codigoUsuario,
              isNew: false,
              timestamp: new Date().getTime(),
            },
          });
        }
      } else {
        showToast(
          response.data.message || "Erro ao atualizar usuário",
          "error"
        );
      }
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async () => {
      return axios.delete(`/api/usuario/${id}`);
    },
    onError: getMutationErrorHandler(
      `Você não possui permissão para ${
        modalOperation === "delete" ? "excluir" : "restaurar"
      } usuários`,
      (error) => {
        setModalOperation(null);
        showToast(
          error.response?.data?.message || "Erro ao atualizar usuário",
          "error"
        );
        setModalOperation(null);
      }
    ),
    onSuccess: (response) => {
      if (response.data.isSuccess) {
        if (modalOperation === "restore") {
          // User was restored
          showToast(
            <>
              Usuário <b>{formMethods.getValues("codigo")}</b> restaurado com
              sucesso!
            </>,
            "success"
          );
          refetch();
          setModalOperation(null);
        } else {
          // User was deleted
          navigate("/cadastros/usuarios", {
            state: {
              deleteSuccess: true,
              deletedName: formMethods.getValues("nome"),
              deletedCode: formMethods.getValues("codigo"),
              timestamp: new Date().getTime(),
            },
          });
        }
      } else {
        showToast(
          response.data.message ||
            (modalOperation === "delete"
              ? "Erro ao excluir usuário"
              : "Erro ao restaurar usuário"),
          "error"
        );
        setModalOperation(null);
      }
    },
  });

  const handleDeleteOrRestore = (isDelete: boolean) => {
    setModalOperation(isDelete ? "delete" : "restore");
  };

  const handleConfirmOperation = async () => {
    if (modalOperation === "delete" && data?.isDeleted) {
      // If we're trying to delete an already deleted user, change to restore
      setModalOperation("restore");
    }
    await deleteUserMutation.mutate();
  };

  const handleCancelOperation = () => {
    setModalOperation(null);
  };

  const togglePdvNovaSenhaVisibility = () => {
    setShowPdvNovaSenha(!showPdvNovaSenha);
  };

  const togglePdvConfirmarSenhaVisibility = () => {
    setShowPdvConfirmarSenha(!showPdvConfirmarSenha);
  };

  const toggleFpPasswordVisibility = () => {
    setShowFpPassword(!showFpPassword);
  };

  const toggleSngpcPasswordVisibility = () => {
    setShowSngpcPassword(!showSngpcPassword);
  };

  const handleSubmit: SubmitHandler<FormValues> = async (formData) => {
    if (formData.pdvNovaSenha) {
      formData.pdvSenha = formData.pdvNovaSenha;
    }

    await updateUserMutation.mutate(formData);
  };

  useEffect(() => {
    if (!updateUserMutation.isPending) {
      // If the mutation was successful, ensure the form is not marked as dirty
      if (updateUserMutation.isSuccess) {
        isDirtyRef.current = false;
      }
    }
  }, [updateUserMutation.isPending, updateUserMutation.isSuccess]);

  const handleLogoutNow = async () => {
    await logout();
  };

  const handleLogoutLater = () => {
    setShowLogoutModal(false);
    navigate("/cadastros/usuarios", {
      state: {
        successCode: formMethods.getValues("codigo"),
        isNew: false,
        timestamp: new Date().getTime(),
      },
    });
  };

  if (isLoading) {
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Carregando...</span>
      </Spinner>
    );
  }

  if (isError) {
    return <div>Ocorreu um erro ao carregar os dados do usuário</div>;
  }

  return (
    <div>
      <UnsavedChangesModal
        show={showNavigationModal}
        onConfirm={handleNavigationConfirm}
        onCancel={handleNavigationCancel}
      />

      <LogoutModal
        show={showLogoutModal}
        onLogoutNow={handleLogoutNow}
        onLogoutLater={handleLogoutLater}
      />

      <DeleteRestoreModal
        show={modalOperation !== null}
        isDeleteOperation={modalOperation === "delete"}
        onConfirm={handleConfirmOperation}
        onCancel={handleCancelOperation}
        isLoading={deleteUserMutation.isPending}
        itemName={formMethods.getValues("nome")}
        itemCode={formMethods.getValues("codigo")}
        profileImage={formMethods.getValues("imageUrl")}
      />

      <PageBreadcrumb items={usuarioBreadcrumbItems()} />

      <FormProvider {...formMethods}>
        <Form
          noValidate
          className="mb-9"
          onSubmit={formMethods.handleSubmit(handleSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Editar um Usuário</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Atualize as informações do usuário
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="outline-danger"
                className="me-2"
                onClick={() => handleDeleteOrRestore(true)}
              >
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Excluir
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={updateUserMutation.isPending}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {updateUserMutation.isPending
                  ? "Salvando..."
                  : "Salvar Alterações"}
              </Button>
            </div>
          </div>

          {data?.isDeleted && (
            <DeletedItemBanner
              title="Este usuário foi excluído e não tem mais acesso ao sistema"
              message="Todas as informações são apresentadas apenas para referência. Para editar o usuário, restaure o acesso primeiro."
              deletedAt={data?.deletedAt}
              onRestore={() => handleDeleteOrRestore(false)}
              restoreButtonText="Restaurar Acesso"
              badgeText="Usuário Inativo"
            />
          )}

          <InformacoesUsuario />

          <DeletedBorderCard isDeleted={data?.isDeleted}>
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faAddressCard} className="me-2" />
                Permissões e Configurações
              </h5>
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex flex-column gap-3">
                    <div className="border-start border-4 ps-3 mb-3">
                      <AcessoAoSistema disabled={data?.isDeleted} />
                    </div>

                    <div className="border-start border-4 ps-3">
                      <h6 className="fw-bold mb-3">Funções do Usuário</h6>
                      <Form.Check
                        type="switch"
                        id="isEntregador"
                        label="Permite selecionar usuário como entregador"
                        className="mb-2"
                        {...formMethods.register("isEntregador")}
                        disabled={data?.isDeleted}
                      />
                      <Form.Check
                        type="switch"
                        id="isVendedor"
                        label="Permite selecionar usuário como vendedor"
                        className="mb-2"
                        {...formMethods.register("isVendedor")}
                        disabled={data?.isDeleted}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="border-start border-4 ps-3">
                    <PermissoesPdv disabled={data?.isDeleted} />
                  </div>
                </div>
              </div>
            </Card.Body>
          </DeletedBorderCard>

          <DeletedBorderCard isDeleted={data?.isDeleted}>
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                Dados para acesso à Farmácia Popular
              </h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="farmaciaPopularUsuario">
                    <Form.Label>Usuário Farmácia Popular</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Nome de usuário"
                      {...formMethods.register("fpUsuario")}
                      disabled={data?.isDeleted}
                    />
                    <Form.Text className="text-muted">
                      Usuário de acesso ao sistema Farmácia Popular
                    </Form.Text>
                  </Form.Group>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="farmaciaPopularSenha">
                    <Form.Label>Senha Farmácia Popular</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showFpPassword ? "text" : "password"}
                        placeholder="Senha para Farmácia Popular"
                        {...formMethods.register("fpSenha")}
                        disabled={data?.isDeleted}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={toggleFpPasswordVisibility}
                        className="rounded-end"
                        disabled={data?.isDeleted}
                      >
                        <FontAwesomeIcon
                          icon={showFpPassword ? faEyeSlash : faEye}
                        />
                      </Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Senha para acesso ao sistema Farmácia Popular
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            </Card.Body>
          </DeletedBorderCard>

          <DeletedBorderCard isDeleted={data?.isDeleted}>
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faFlask} className="me-2" />
                Acesso ao SNGPC / ANVISA
              </h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="emailSngpc">
                    <Form.Label>Email SNGPC</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="email@registro.anvisa.gov.br"
                      {...formMethods.register("emailSngpc")}
                      disabled={data?.isDeleted}
                    />
                    <Form.Text className="text-body-secondary">
                      Email de acesso ao portal SNGPC/ANVISA
                    </Form.Text>
                  </Form.Group>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="senhaSngpc">
                    <Form.Label>Senha SNGPC</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showSngpcPassword ? "text" : "password"}
                        placeholder="Deixe em branco para não alterar"
                        {...formMethods.register("senhaSngpc")}
                        disabled={data?.isDeleted}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={toggleSngpcPasswordVisibility}
                        className="rounded-end"
                        disabled={data?.isDeleted}
                      >
                        <FontAwesomeIcon
                          icon={showSngpcPassword ? faEyeSlash : faEye}
                        />
                      </Button>
                    </InputGroup>
                    <Form.Text className="text-body-secondary">
                      Senha para acesso ao portal SNGPC/ANVISA
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            </Card.Body>
          </DeletedBorderCard>

          <DeletedBorderCard isDeleted={data?.isDeleted}>
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                Dados para acesso do PDV
              </h5>

              <div className="row g-3">
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="pdvNovaSenha">
                    <Form.Label>
                      <FontAwesomeIcon icon={faKey} className="me-1" />
                      Nova Senha PDV
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPdvNovaSenha ? "text" : "password"}
                        placeholder="Digite a nova senha do PDV"
                        {...formMethods.register("pdvNovaSenha")}
                        isInvalid={!!formMethods.formState.errors.pdvNovaSenha}
                        disabled={data?.isDeleted}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={togglePdvNovaSenhaVisibility}
                        className="rounded-end"
                        disabled={data?.isDeleted}
                      >
                        <FontAwesomeIcon
                          icon={showPdvNovaSenha ? faEyeSlash : faEye}
                        />
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {formMethods.formState.errors.pdvNovaSenha?.message}
                      </Form.Control.Feedback>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Deixe em branco para manter a senha atual
                    </Form.Text>
                  </Form.Group>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="pdvConfirmarSenha">
                    <Form.Label>
                      <FontAwesomeIcon icon={faKey} className="me-1" />
                      Confirmar Senha PDV
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPdvConfirmarSenha ? "text" : "password"}
                        placeholder="Confirme a senha do PDV"
                        {...formMethods.register("pdvConfirmarSenha", {
                          validate: {
                            matchesPdvPassword: (value) => {
                              const novaSenha =
                                formMethods.getValues("pdvNovaSenha");
                              // Only validate if nova senha has a value
                              if (!novaSenha) return true;
                              // Check that both values match exactly
                              return (
                                novaSenha === value ||
                                "As senhas não correspondem"
                              );
                            },
                          },
                        })}
                        isInvalid={
                          !!formMethods.formState.errors.pdvConfirmarSenha
                        }
                        disabled={data?.isDeleted}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={togglePdvConfirmarSenhaVisibility}
                        className="rounded-end"
                        disabled={data?.isDeleted}
                      >
                        <FontAwesomeIcon
                          icon={showPdvConfirmarSenha ? faEyeSlash : faEye}
                        />
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {
                          formMethods.formState.errors.pdvConfirmarSenha
                            ?.message
                        }
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </div>
              </div>
            </Card.Body>
          </DeletedBorderCard>

          <LojasAssociadas isDeleted={data?.isDeleted} />

          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
             <Button
              variant="secondary"
              type="button"
              onClick={() =>
                navigate("/cadastros/usuarios", {
                  state: { needsRefresh: true },
                })
              }
            >
              Cancelar
            </Button>
            <div className="d-flex flex-wrap gap-2">
              {!data?.isDeleted && (
              <div>
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={() => handleDeleteOrRestore(true)}
                  className="me-2"
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Excluir Usuário
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={updateUserMutation.isPending}
                >
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  {updateUserMutation.isPending
                    ? "Salvando..."
                    : "Salvar Alterações"}
                </Button>
              </div>
            )}
              {data?.isDeleted && (
                <Button
                  variant="danger"
                  onClick={() => handleDeleteOrRestore(false)}
                  className="rounded-pill px-4"
                >
                  <>
                    <FontAwesomeIcon icon={faUnlock} className="me-2" />
                    Restaurar Usuário
                  </>
                </Button>
              )}
            </div>
          </div>
          {/* <div className="d-flex justify-content-between mt-4">
          
            {!data?.isDeleted && (
              <div>
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={() => handleDeleteOrRestore(true)}
                  className="me-2"
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Excluir Usuário
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={updateUserMutation.isPending}
                >
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  {updateUserMutation.isPending
                    ? "Salvando..."
                    : "Salvar Alterações"}
                </Button>
              </div>
            )}
            {data?.isDeleted && (
              <Button
                variant="danger"
                onClick={() => handleDeleteOrRestore(false)}
                className="rounded-pill px-4"
              >
                <>
                  <FontAwesomeIcon icon={faUnlock} className="me-2" />
                  Restaurar Usuário
                </>
              </Button>
            )}
          </div> */}
        </Form>
      </FormProvider>
    </div>
  );
};

export default EditarUsuario;
